const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.bettermedaily.co.za/api';

const getDeviceContext = () => {
  let deviceId = '';

  try {
    deviceId = localStorage.getItem('betterme_affiliate_device_id') || '';
    if (!deviceId) {
      deviceId = (window.crypto?.randomUUID?.() || `affiliate-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`);
      localStorage.setItem('betterme_affiliate_device_id', deviceId);
    }
  } catch (error) {
    deviceId = `affiliate-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }

  return {
    deviceId,
    devicePlatform: navigator.platform || 'web',
    deviceModel: navigator.vendor || navigator.userAgent || 'unknown',
    userAgent: navigator.userAgent || '',
  };
};

class ApiService {
  constructor() {
    this.token = localStorage.getItem('betterpath_token');
    this.authListeners = new Set();
    this.ensureValidToken();
  }

  getStoredToken() {
    return this.token || localStorage.getItem('betterpath_token');
  }

  isTokenExpired(token) {
    if (!token) {
      return true;
    }

    try {
      const payloadSegment = token.split('.')[1];
      if (!payloadSegment) {
        return false;
      }

      const normalized = payloadSegment.replace(/-/g, '+').replace(/_/g, '/');
      const decodedPayload = JSON.parse(window.atob(normalized));

      if (!decodedPayload?.exp) {
        return false;
      }

      return decodedPayload.exp * 1000 <= Date.now();
    } catch (error) {
      console.warn('Failed to inspect affiliate token expiry:', error);
      return false;
    }
  }

  ensureValidToken() {
    const token = this.getStoredToken();
    if (!token) {
      return false;
    }

    if (this.isTokenExpired(token)) {
      this.clearToken();
      return false;
    }

    if (!this.token) {
      this.token = token;
    }

    return true;
  }

  isAuthenticated() {
    return this.ensureValidToken();
  }

  notifyAuthListeners() {
    this.authListeners.forEach((listener) => listener(this.isAuthenticated()));
  }

  subscribeAuth(listener) {
    this.authListeners.add(listener);
    listener(this.isAuthenticated());

    return () => {
      this.authListeners.delete(listener);
    };
  }

  setToken(token) {
    this.token = token;
    localStorage.setItem('betterpath_token', token);
    this.notifyAuthListeners();
  }

  clearToken() {
    this.token = null;
    localStorage.removeItem('betterpath_token');
    this.notifyAuthListeners();
  }

  logout() {
    this.clearToken();
  }

  async request(endpoint, options = {}, config = {}) {
    const { skipAuthRedirect = false } = config;
    const hasValidToken = this.ensureValidToken();
    const headers = {
      'Content-Type': 'application/json',
      'ngrok-skip-browser-warning': '1',
      ...options.headers,
    };

    if (hasValidToken && this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    } else if (!skipAuthRedirect && endpoint !== '/auth/login-affiliate') {
      window.location.href = '/login';
      throw new Error('Session expired. Please sign in again.');
    }

    try {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        ...options,
        headers,
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorJson;
        try {
          errorJson = errorText ? JSON.parse(errorText) : null;
        } catch (parseErr) {
          console.warn('Failed to parse error response:', parseErr);
        }
        const errorMessage = errorJson?.message || response.statusText || 'API request failed';

        if (response.status === 401 && !skipAuthRedirect) {
          this.clearToken();
          window.location.href = '/login';
          throw new Error('Unauthorized - redirecting to login');
        }

        throw new Error(errorMessage);
      }

      if (response.status === 204 || response.status === 205) {
        return null;
      }

      const text = await response.text();
      if (!text) {
        return null;
      }

      return JSON.parse(text);
    } catch (error) {
      console.error('API Error:', error);
      throw error;
    }
  }

  // Auth methods copied from main app
  async register(email, password, name) {
    const data = await this.request(
      '/auth/register',
      {
        method: 'POST',
        body: JSON.stringify({ email, password, name }),
      },
      { skipAuthRedirect: true }
    );
    this.setToken(data.token);
    return data;
  }

  async registerAffiliateUser(email, password, name, hasAcceptedTerms = false, phoneNumber = '', howDidYouHearAboutUs = '', totalAudienceOnSocialMedia = null) {
    const deviceContext = getDeviceContext();
    const data = await this.request(
      '/auth/register-affiliate',
      {
        method: 'POST',
        body: JSON.stringify({
          email,
          password,
          name,
          hasAcceptedTerms,
          phoneNumber,
          howDidYouHearAboutUs,
          totalAudienceOnSocialMedia,
          ...deviceContext,
        }),
      },
      { skipAuthRedirect: true }
    );
    this.setToken(data.token);
    return data;
  }

  async login(email, password) {
    const data = await this.request(
      '/auth/login-affiliate',
      {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      },
      { skipAuthRedirect: true }
    );
    this.setToken(data.token);
    return data;
  }

  async googleLogin(idToken) {
    const data = await this.request(
      '/auth/google',
      {
        method: 'POST',
        body: JSON.stringify({ idToken }),
      },
      { skipAuthRedirect: true }
    );
    this.setToken(data.token);
    return data;
  }

  async sendVerificationEmail(email, role = 'Influencer') {
    return this.request(
      '/auth/send-verification-email',
      {
        method: 'POST',
        body: JSON.stringify({ email, role }),
      },
      { skipAuthRedirect: true }
    );
  }

  async verifyEmail(token) {
    return this.request(
      '/auth/verify-email',
      {
        method: 'POST',
        body: JSON.stringify({ token }),
      },
      { skipAuthRedirect: true }
    );
  }

  async forgotPassword(email, role = 'Influencer') {
    return this.request(
      '/auth/forgot-password',
      {
        method: 'POST',
        body: JSON.stringify({ email, role }),
      },
      { skipAuthRedirect: true }
    );
  }

  async resetPassword(token, newPassword, role = 'Influencer') {
    return this.request(
      '/auth/reset-password',
      {
        method: 'POST',
        body: JSON.stringify({ token, newPassword, role }),
      },
      { skipAuthRedirect: true }
    );
  }

  // User Profile
  async getCurrentUser() {
    return this.request('/auth/me');
  }

  async updateProfile(updates) {
    return this.request('/auth/update-profile', {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  // Affiliate specific methods
  async registerAffiliate(affiliateData) {
    return affiliateData;
  }

  async getAffiliateDashboard() {
    return this.request('/referrals/dashboard');
  }

  async getPricing() {
    return this.request('/settings/pricing', {}, { skipAuthRedirect: true });
  }

  async getAffiliateReferrals(page = 1, pageSize = 10) {
    return this.request(`/referrals?page=${page}&pageSize=${pageSize}`);
  }

  async getAffiliatePayouts() {
    return this.request('/referrals/payout-requests');
  }

  async requestPayout(payoutData) {
    return this.request('/referrals/payout-requests', {
      method: 'POST',
      body: JSON.stringify(payoutData),
    });
  }
}

export default new ApiService();
